/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {getDisplayText} from './commons';
import {
    EntityAttributeProps,
    EntityAttributeState,
    ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    EntityAttribute,
    EntityAttributeStateEnum,
} from './EntityAttribute';

/**
 * State for this component
 */
export interface EnumEntityAttributeState extends EntityAttributeState {
    /**
     * Index of the selected value
     */
    selectedIndex: number;
}

/**
 * Parameters for an attribute with enumerable valid values
 */
export interface EnumEntityAttributeProps extends EntityAttributeProps {
    /**
     * The valid values for the attribute
     */
    values: any[];
    /**
     * Event when the value for the attribute is changed by the user
     * @param component The current component (this)
     * @param event The original HTML input event
     */
    onFieldEditEnum: (
        component: EnumEntityAttribute,
        event: React.FormEvent<HTMLSelectElement>
    ) => void;
}

/**
 * The default props for a enum entity attribute
 */
export const ENUM_ENTITY_ATTRIBUTE_DEFAULT_PROPS = {
    ...ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    onFieldEditEnum: (
        component: EnumEntityAttribute,
        event: React.FormEvent<HTMLSelectElement>
    ) => {
        if (component.props.disabled === true) {
            return;
        }
        let index = event.currentTarget.selectedIndex - 1;
        let value = index >= 0 ? component.props.values[index] : null;
        let error = component.props.validateValue(
            component.props.entity,
            component.props.attribute,
            component.props.isRequired(component.props.attribute),
            value
        );
        if (error !== null) {
            component.setState({selectedIndex: index, validationError: error});
        } else {
            component.setState({
                selectedIndex: index,
                validationError: component.props.onChange(
                    component.props.entity,
                    component.props.attribute,
                    component.props.isRequired(component.props.attribute),
                    value
                ),
            });
        }
    },
    renderInput: (component: EnumEntityAttribute) => {
        return (
            <select
                value={
                    component.state.selectedIndex >= 0
                        ? component.state.selectedIndex
                        : ''
                }
                onChange={(event) =>
                    component.props.onFieldEditEnum(component, event)
                }
                disabled={component.props.disabled}
            >
                <option value={''}>
                    {component.context.translate('-- select --')}
                </option>
                {component.props.values.map((value: any, index: number) => (
                    <option key={'value-' + index} value={index}>
                        {getDisplayText(value)}
                    </option>
                ))}
            </select>
        );
    },
};

/**
 * Builds a state from the given props
 * @param props The current props
 */
export function getEnumEntityAttributeStateFromProps(
    props: EnumEntityAttributeProps
): EnumEntityAttributeState {
    let value = props.getter(props.entity, props.attribute);
    let selectedIndex = props.values.indexOf(value);
    return {
        valueReference: value,
        value: value,
        selectedIndex: selectedIndex,
        validationError: props.validateValue(
            props.entity,
            props.attribute,
            props.isRequired(props.attribute),
            value
        ),
        savingState: EntityAttributeStateEnum.idle,
    };
}

/**
 * Component for rendering an entity attribute with enumerable valid values
 */
export class EnumEntityAttribute extends EntityAttribute<
    EnumEntityAttributeProps,
    EnumEntityAttributeState
> {
    public static defaultProps = ENUM_ENTITY_ATTRIBUTE_DEFAULT_PROPS;
    state: EnumEntityAttributeState;
    timeout: number | undefined;
    _isMounted: boolean;

    constructor(props: Readonly<EnumEntityAttributeProps>) {
        super(props);
        super(props);
        this.timeout = undefined;
        this._isMounted = false;
        this.state = getEnumEntityAttributeStateFromProps(props);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(
        prevProps: EntityAttributeProps,
        prevState: EntityAttributeState,
        snapshot: any
    ) {
        if (this.props.entity !== prevProps.entity) {
            this.setState(getEnumEntityAttributeStateFromProps(this.props));
        }
    }

    render(): JSX.Element {
        return this.props.render(this);
    }
}
